import { groundQuantityOf, type EntityState, type Item, type PickUpRefusalReason } from "odyssey-shared";
import type { GroundLook } from "./find.ts";
import type { GroundReading } from "./view.ts";
import { countdownClock } from "../hud/countdown-clock.ts";
import { say } from "../../words.ts";

/**
 * The line a refused pick-up says.
 *
 * @param reason The server's reason.
 * @param waitMs The server's wait until the item turns public, read for `owned` alone and shown in whole seconds,
 *   rounded up, so the line never says 0:00 while the item is still owned.
 * @returns The line, or null for a reason that says nothing.
 * @example
 * pickUpRefusalLine("owned", 191_050);
 * // "That doesn't belong to you (3:12)."
 */
export function pickUpRefusalLine(reason: PickUpRefusalReason, waitMs: number): string | null {
  if (reason === "owned") {
    return say("hud.pickUp.owned", { clock: countdownClock(Math.ceil(waitMs / 1000)) });
  }
  return reason === "full" ? say("hud.pickUp.full") : null;
}

/**
 * A ground item's label (P2.8-T85; the author, 2026-10-10: "i drop 4 health pots, it doesnt show as x4"): the item's
 * name, with "x" and the count above 1, as the loot toast writes a stack.
 *
 * @param name The item's name, as the rules name it.
 * @param quantity The units the item holds.
 * @returns The label.
 * @example
 * groundLabelName("Minor Healing Potion", 4);
 * // "Minor Healing Potion x4"
 */
export function groundLabelName(name: string, quantity: number): string {
  return quantity > 1 ? `${name} x${quantity}` : name;
}

/**
 * A drawn ground item's reading (P2.8-T76, P2.8-T85): its quantity read from the record's Health and PvP bytes, and its
 * label's name, the item's with "x" and the count above 1.
 *
 * @param state The item's record.
 * @param item The item by the page's rules.
 * @param look Its look.
 * @param x Its point across, in world pixels.
 * @param y Its point down, in world pixels.
 * @returns The reading, lifted 0.
 * @example
 * groundReadingOf(state, potion, "own", 64, 96).name;
 * // "Minor Healing Potion x4" for a record holding 4
 */
export function groundReadingOf(state: Pick<EntityState, "handle" | "health" | "pvp">, item: Pick<Item, "id" | "name" | "rarity">, look: GroundLook, x: number, y: number): GroundReading {
  const quantity = groundQuantityOf(state);
  return { handle: state.handle, item: item.id, quantity, name: groundLabelName(item.name, quantity), rarity: item.rarity, look, x, y, lift: 0 };
}
